import { autoinject, bindable, Container } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { PubSub } from 'lib/event/PubSub';
import { AsyncErrorHandler, ComponentLoadingFader } from 'lib/ui';
import { MooringCage, MooringComponentType, MooringReviewDto, UpdateMooringCageDto } from 'models/mooring';
import { ToastService } from 'services';
import { MooringOfferService, MooringService } from 'services/mooring';
import { OfferViewContext } from '../../OfferViewContext';
import { ConfirmDialog } from 'components/dialogs/confirm/confirm-dialog';

@autoinject
export class CagesContainer {
  @bindable
  protected mooringId: number;
  protected review: MooringReviewDto;

  protected context = OfferViewContext.useContext(this.container);

  @bindable
  protected reviewId?: number;

  protected cages: MooringCage[] = [];

  constructor(
    private container: Container,
    protected element: Element,
    private mooringService: MooringService,
    private toaster: ToastService,
    private t: I18N,
    private pubsub: PubSub,
    private confirmation: ConfirmDialog,
    private mooringOfferService: MooringOfferService
  ) {
    pubsub.sub('entity:updated', async () => {
      void this.getCages();
    });
    pubsub.sub('list-entity:updated', async () => {
      void this.getCages();
    });
  }

  protected controlView: boolean;
  protected toggleControlView() {
    this.controlView = !this.controlView;
  }

  @AsyncErrorHandler
  @ComponentLoadingFader
  async attached() {
    if (!this.mooringId) {
      throw new Error('mooringId is required => mooring-id.bind="<id>"');
    }

    await this.getCages();
  }

  @AsyncErrorHandler
  protected async getCages() {
    const { data } = await this.mooringService.getCages(this.mooringId);
    if (data?.length) {
      setTimeout(() => {
        this.cages = data;
      }, 10);
    }

    this.review = this.context.getState().offerReview;
  }

  @AsyncErrorHandler
  protected async onSaveCages({ entries, canNavigateOnSave }: { entries: MooringCage[]; canNavigateOnSave: boolean }) {
    const dto = entries.map(
      (cage) =>
        ({
          Id: cage.Id,
          Row: cage.Row,
          Column: cage.Column,
          MoorlogNo: cage.MoorlogNo,
          AnalysisNo: cage.AnalysisNo,
          PenCircumference: cage.PenCircumference,
          FloaterId: cage.FloaterId,
        }) satisfies UpdateMooringCageDto
    );

    await this.mooringService.updateCages(this.mooringId, dto);
    this.toaster.showUpdated();
    this.pubsub.publish('list-entity:updated', { name: MooringComponentType.cage });
    if (canNavigateOnSave) {
      this.pubsub.publish('tabs:change-guard-ok', null);
    } else {
      await this.getCages();
    }
  }

  protected async setReviewStatus(reviewed: boolean) {
    if (!this.reviewId) {
      return;
    }

    const confirmed = await this.confirmation.confirmYesCancel(
      'dialog.areYouSure',
      reviewed
        ? this.t.tr('general.markAsCheckedConfirmationText', { x: this.t.tr('mooring.floater_plural').toLowerCase() })
        : this.t.tr('general.markAsUnCheckedConfirmationText', { x: this.t.tr('mooring.floater_plural').toLowerCase() })
    );

    if (!confirmed) return;

    await this.mooringOfferService.updateReviewStep(this.reviewId, { Step: 'CAGES', Reviewed: reviewed });
    this.toaster.showSuccess(reviewed ? 'mooring.setAsChecked' : 'mooring.setAsNotChecked');
    this.pubsub.publish('entity-parent:updated', null);
  }

  protected unbind() {
    this.pubsub.unsub();
  }

  protected detached() {
    this.pubsub.unsub();
  }
}
